import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart3, Megaphone } from "lucide-react";

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

// Global function to open cookie settings
let openCookieSettingsCallback: (() => void) | null = null;

export const openCookieSettings = () => {
  if (openCookieSettingsCallback) {
    openCookieSettingsCallback();
  }
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Load saved preferences
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPrefs) {
      try {
        setPreferences(JSON.parse(savedPrefs));
      } catch {
        // ignore
      }
    }

    // Register callback for reopening
    openCookieSettingsCallback = () => {
      setShowBanner(true);
      setShowPreferences(true);
      setTimeout(() => setIsAnimating(true), 10);
    };

    // Show banner on first visit - delayed to avoid impacting LCP
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
        setIsAnimating(true);
      }, 4000);
      return () => clearTimeout(timer);
    }

    return () => {
      openCookieSettingsCallback = null;
    };
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(COOKIE_CONSENT_KEY, "custom");
    
    // Dispatch custom event to notify analytics hook
    window.dispatchEvent(new CustomEvent("cookie-preferences-updated"));
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setIsAnimating(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setIsAnimating(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setIsAnimating(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleClose = () => {
    handleRejectAll();
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 transition-all duration-300 ${
        isAnimating ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div className="container mx-auto max-w-4xl">
        <div className="relative bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Sluiten"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-2 flex-1 pr-8">
                  <h3 className="text-lg font-semibold text-foreground">
                    Wij gebruiken cookies 🍪
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Wij gebruiken cookies om uw ervaring te verbeteren, onze website te analyseren en 
                    gepersonaliseerde content te tonen. U kunt hieronder uw voorkeuren instellen.{" "}
                    <Link to="/cookies" className="text-primary hover:underline font-medium">
                      Lees ons cookiebeleid
                    </Link>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-fit"
              >
                {showPreferences ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showPreferences ? "Voorkeuren verbergen" : "Voorkeuren aanpassen"}
              </button>

              {showPreferences && (
                <div className="space-y-4 border-t border-border pt-6 animate-fade-in">
                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/50">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-foreground">Noodzakelijke cookies</h4>
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            Altijd actief
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Essentieel voor het functioneren van de website. Deze cookies kunnen niet worden uitgeschakeld.
                        </p>
                      </div>
                    </div>
                    <Switch checked={true} disabled className="opacity-50" />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/50">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Analytische cookies</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Helpen ons te begrijpen hoe bezoekers de website gebruiken, zodat we deze kunnen verbeteren.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.analytics}
                      onCheckedChange={() => togglePreference("analytics")}
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/50">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                        <Megaphone className="w-5 h-5 text-orange-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Marketing cookies</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Worden gebruikt om gepersonaliseerde advertenties te tonen en de effectiviteit van campagnes te meten.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.marketing}
                      onCheckedChange={() => togglePreference("marketing")}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="w-full sm:w-auto order-3 sm:order-1"
                >
                  Alles weigeren
                </Button>
                {showPreferences && (
                  <Button
                    variant="secondary"
                    onClick={handleSavePreferences}
                    className="w-full sm:w-auto order-2"
                  >
                    Voorkeuren opslaan
                  </Button>
                )}
                <Button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto order-1 sm:order-3 sm:ml-auto bg-primary hover:bg-primary/90"
                >
                  Alles accepteren
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
